import React, { useState, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useLocation, useNavigate } from "react-router-dom";
import { BASE_URL } from "../constants";

const VerifyOtp = () => {
  const location = useLocation();
  const { StateCode, OrgCode, UserCode, OrgEmail } = location.state || {};
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const inputRefs = useRef([]);

  const passwordCriteria = [
    {
      id: "length",
      label: "At least 8 characters",
      valid: newPassword.length >= 8,
    },
    {
      id: "uppercase",
      label: "At least one uppercase letter (A-Z)",
      valid: /[A-Z]/.test(newPassword),
    },
    {
      id: "lowercase",
      label: "At least one lowercase letter (a-z)",
      valid: /[a-z]/.test(newPassword),
    },
    {
      id: "number",
      label: "At least one number (0-9)",
      valid: /\d/.test(newPassword),
    },
    {
      id: "special",
      label: "At least one special character (!@#$%^&*...)",
      valid: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(newPassword),
    },
  ];

  const isPasswordValid = passwordCriteria.every((item) => item.valid);
  const doPasswordsMatch =
    confirmPassword.length > 0 && newPassword === confirmPassword;

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return; // Allow only digits

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1].focus(); // Move to next input
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus(); // Move back on empty backspace
    }
  };

  const handleVerifyOtp = async () => {
    setError("");
    setMessage("");

    const enteredOtp = otp.join("");

    // ✅ Validation
    if (enteredOtp.length < 6) {
      return setError("Please enter a valid 6-digit OTP.");
    }
    // Secure password validation
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]).{8,}$/;

    if (!newPassword) {
      return setError("Password is required.");
    }

    if (!passwordRegex.test(newPassword)) {
      return setError(
        "Password must be at least 8 characters long and contain " +
          "at least one uppercase letter, one lowercase letter, " +
          "one number, and one special character.",
      );
    }

    if (newPassword !== confirmPassword) {
      return setError("Passwords do not match.");
    }

    if (!StateCode.trim()) {
      return setError("State Code is required.");
    }
    if (!OrgCode.trim()) {
      return setError("Organization ID is required.");
    }
    if (!UserCode.trim()) {
      return setError("User ID is required.");
    }
    if (!OrgEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(OrgEmail)) {
      return setError("Please enter a valid email address.");
    }

    const verifyCred = {
      otp: enteredOtp,
      newPassword: newPassword,
      confirmPassword: confirmPassword,
      userId: `${StateCode}-${OrgCode}-${UserCode}`,
      email: OrgEmail,
    };

    try {
      const response = await fetch(`${BASE_URL}api/user/common/verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(verifyCred),
      });
      const data = await response.json();
      console.log(data);
      if (data.success) {
        window.dispatchEvent(
          new CustomEvent("litss:password-changed", {
            detail: { userId: verifyCred.userId },
          }),
        );
        try {
          sessionStorage.removeItem("litss_password_expiry_snooze");
        } catch {
          // ignore
        }
        alert(data.message);
        navigate("/user-login");
      }
      if (!data.success) {
        setError(data.message);
      }
    } catch (error) {
      setError("Invalid or expired OTP. Please try again.");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundColor: "#f0f4f8",
      }}
    >
      <Paper
        elevation={6}
        sx={{
          padding: 4,
          maxWidth: 450,
          width: "100%",
          borderRadius: "12px",
          boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.15)",
          backgroundColor: "white",
        }}
      >
        <Typography variant="h5" align="center" sx={{ color: "#333", mb: 2 }}>
          Verify OTP & Reset Password
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        {/* OTP Input Boxes */}
        <Box
          sx={{ display: "flex", justifyContent: "center", gap: 1.5, mb: 3 }}
        >
          {otp.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(el) => (inputRefs.current[index] = el)}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              variant="outlined"
              size="small"
              inputProps={{
                maxLength: 1,
                style: {
                  textAlign: "center",
                  fontSize: "1.5rem",
                  width: "50px",
                  height: "50px",
                  borderRadius: "8px",
                  fontWeight: "bold",
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                },
              }}
            />
          ))}
        </Box>

        {/* Password Fields with Live Validation */}
        <TextField
          label="New Password"
          type={showNewPassword ? "text" : "password"}
          fullWidth
          variant="outlined"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  edge="end"
                  aria-label="toggle password visibility"
                >
                  {showNewPassword ? (
                    <VisibilityOff sx={{ fontSize: 20 }} />
                  ) : (
                    <Visibility sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: newPassword ? 1 : 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              borderColor:
                newPassword.length > 0
                  ? isPasswordValid
                    ? "#16A34A"
                    : "#DC2626"
                  : undefined,
            },
          }}
        />

        {/* Live Password Criteria Display */}
        {newPassword.length > 0 && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: "8px",
              backgroundColor: isPasswordValid ? "#F0FDF4" : "#FEF2F2",
              border: `1px solid ${isPasswordValid ? "#BBF7D0" : "#FECACA"}`,
              transition: "all 0.2s ease",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: isPasswordValid ? "#15803D" : "#991B1B",
                display: "block",
                mb: 0.8,
                fontSize: "0.8rem",
              }}
            >
              Password Requirements:
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
              {passwordCriteria.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                  }}
                >
                  {item.valid ? (
                    <CheckCircleIcon sx={{ fontSize: 16, color: "#16A34A" }} />
                  ) : (
                    <CancelIcon sx={{ fontSize: 16, color: "#DC2626" }} />
                  )}
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: item.valid ? 600 : 500,
                      color: item.valid ? "#15803D" : "#DC2626",
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <TextField
          label="Confirm Password"
          type={showConfirmPassword ? "text" : "password"}
          fullWidth
          variant="outlined"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  edge="end"
                  aria-label="toggle confirm password visibility"
                >
                  {showConfirmPassword ? (
                    <VisibilityOff sx={{ fontSize: 20 }} />
                  ) : (
                    <Visibility sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: confirmPassword ? 1 : 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              borderColor:
                confirmPassword.length > 0
                  ? doPasswordsMatch
                    ? "#16A34A"
                    : "#DC2626"
                  : undefined,
            },
          }}
        />

        {/* Live Confirm Password Match Indicator */}
        {confirmPassword.length > 0 && (
          <Box
            sx={{
              mb: 2.5,
              p: 1,
              px: 1.5,
              borderRadius: "8px",
              backgroundColor: doPasswordsMatch ? "#F0FDF4" : "#FEF2F2",
              border: `1px solid ${doPasswordsMatch ? "#BBF7D0" : "#FECACA"}`,
              display: "flex",
              alignItems: "center",
              gap: 0.8,
            }}
          >
            {doPasswordsMatch ? (
              <>
                <CheckCircleIcon sx={{ fontSize: 16, color: "#16A34A" }} />
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: "#15803D",
                  }}
                >
                  Passwords match
                </Typography>
              </>
            ) : (
              <>
                <CancelIcon sx={{ fontSize: 16, color: "#DC2626" }} />
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: "#DC2626",
                  }}
                >
                  Passwords do not match
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* New Fields: State Code, Org ID, User ID */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            mb: 2,
          }}
        >
          <TextField
            label="State Code"
            variant="outlined"
            size="small"
            readonly
            disabled
            value={StateCode}
            sx={{ width: "30%" }}
          />
          <TextField
            label="Org Code"
            variant="outlined"
            size="small"
            readonly
            disabled
            value={OrgCode}
            sx={{ width: "30%" }}
          />
          <TextField
            label="User Code"
            variant="outlined"
            size="small"
            readonly
            disabled
            value={UserCode}
            sx={{ width: "30%" }}
          />
        </Box>

        {/* Organization Email */}
        <TextField
          label="Organization Email"
          type="email"
          readonly
          fullWidth
          disabled
          variant="outlined"
          value={OrgEmail}
          sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
        />

        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{
            padding: "12px",
            fontSize: "1rem",
            textTransform: "none",
            borderRadius: "8px",
            background: "linear-gradient(135deg, #6B73FF 0%, #000DFF 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #5a60f2 0%, #000cd1 100%)",
            },
          }}
          onClick={handleVerifyOtp}
        >
          Verify & Reset
        </Button>
      </Paper>
    </Box>
  );
};

export default VerifyOtp;
